<template>
  <modal :title="$t('account_redesign.up_apply_enterprise_title')" :visible.sync='isShowDialog' width='600px' @closed='resetSteps'>
    <div class='warning-banner'>
      <i class='el-icon-warning'></i>
      <span>{{ $t('account_redesign.up_apply_enterprise_tip') }}</span>
    </div>

    <!-- 步骤指示器 -->
    <div class='step-indicator'>
      <div class='step-item'>
        <div class='step-dot' :class='{ active: !companyVerified, done: companyVerified }'>
          <i v-if='companyVerified' class='el-icon-check'></i>
          <span v-else>1</span>
        </div>
        <div class='step-label' :class='{ active: !companyVerified, done: companyVerified }'>{{ $t('accountRedesign.step_verify_company') }}</div>
      </div>
      <div class='step-line' :class='{ done: companyVerified }'></div>
      <div class='step-item'>
        <div class='step-dot' :class='{ active: companyVerified }'>
          <span>2</span>
        </div>
        <div class='step-label' :class='{ active: companyVerified }'>{{ $t('accountRedesign.step_userInfo') }}</div>
      </div>
    </div>

    <el-form
      ref='applyForm'
      :model='applyForm'
      :rules='applyRules'
      label-width='140px'
      label-position='right'
      class='apply-form'
      @submit.native.prevent
    >
      <!-- 步骤 1：验证 Company ID -->
      <template v-if='!companyVerified'>
        <el-form-item :label="$t('account_redesign.up_apply_company_code')" prop='companyCode'>
          <div class='company-verify-row'>
            <el-input
              v-model.trim='applyForm.companyCode'
              maxlength='30'
              :placeholder="$t('accountRedesign.placeholder_company_id')"
            ></el-input>
            <el-button :loading='verifying' @click='verifyCompany'>
              {{ verifying ? $t('accountRedesign.btn_verifying') : $t('accountRedesign.btn_verify') }}
            </el-button>
          </div>
        </el-form-item>

        <!-- 验证通过提示 -->
        <div v-if='verifyErrorMsg' class='verify-error-tip'>
          <i class='el-icon-circle-close'></i>
          <span>{{ verifyErrorMsg }}</span>
        </div>

        <!-- 申请 Company ID 链接（与 Company ID 输入框右对齐） -->
        <div class='apply-link-row'>
          <div class='apply-link' @click='openApplyDialog'>
            {{ $t('accountRedesign.apply_company_link') }}
          </div>
          <!-- 占位按钮，保证与上方 Verify 按钮同宽 -->
          <el-button class='btn-spacer'>{{ $t('accountRedesign.btn_verify') }}</el-button>
        </div>
      </template>

      <!-- 验证通过提示 -->
      <div v-if='companyVerified' class='company-verified-tip'>
        <i class='el-icon-circle-check'></i>
        <span>{{ $t('accountRedesign.company_verified_tip', { name: verifiedCompanyName }) }}</span>
      </div>

      <!-- 步骤 2：用户信息 -->
      <template v-if='companyVerified'>
        <el-form-item :label="$t('account_redesign.up_apply_email')" prop='email'>
          <el-input v-model.trim='applyForm.email' maxlength='100' :disabled='emailFromApply'></el-input>
        </el-form-item>
        <el-form-item :label="$t('account_redesign.up_apply_mobile')" prop='mobile'>
          <el-input v-model.trim='applyForm.mobile' maxlength='20' :disabled='mobileFromApply'></el-input>
        </el-form-item>
        <el-form-item :label="$t('account_redesign.up_apply_password')" prop='password'>
          <el-input v-model.trim='applyForm.password' type='password' show-password autocomplete='off'></el-input>
        </el-form-item>
      </template>
    </el-form>

    <div slot='footer'>
      <el-button type='primary' @click='isShowDialog = false'>{{ $t('public.btn_close') }}</el-button>
      <el-button v-if='companyVerified' type='primary' :loading='submitLoading' @click='handleSubmit'>
        {{ $t('account_redesign.up_apply_confirm') }}
      </el-button>
    </div>

    <!-- 申请 Company ID 弹框 -->
    <el-dialog
      :title="$t('accountRedesign.apply_company_title')"
      :visible.sync='applyDialogVisible'
      width='460px'
      :close-on-click-modal='false'
      append-to-body
      class='apply-company-dialog'
    >
      <p class='apply-dialog-tip'>{{ $t('accountRedesign.apply_submitted_tip') }}</p>
      <el-form
        ref='companyApplyForm'
        :model='companyApplyForm'
        :rules='companyApplyRules'
        label-position='top'
        class='company-apply-form'
      >
        <el-form-item :label="$t('accountRedesign.label_company_name')" prop='companyName'>
          <el-input v-model.trim='companyApplyForm.companyName' :placeholder="$t('accountRedesign.placeholder_company_name')" maxlength='200'></el-input>
        </el-form-item>
        <el-form-item :label="$t('accountRedesign.label_contact_name')" prop='contactName'>
          <el-input v-model.trim='companyApplyForm.contactName' :placeholder="$t('accountRedesign.placeholder_contact_name')" maxlength='80'></el-input>
        </el-form-item>
        <el-form-item :label="$t('accountRedesign.label_contact_mobile')" prop='contactMobile'>
          <el-input v-model.trim='companyApplyForm.contactMobile' :placeholder="$t('accountRedesign.placeholder_contact_mobile')" maxlength='20'></el-input>
        </el-form-item>
        <el-form-item :label="$t('accountRedesign.label_contact_email')" prop='contactEmail'>
          <el-input v-model.trim='companyApplyForm.contactEmail' :placeholder="$t('accountRedesign.placeholder_contact_email')" maxlength='100'></el-input>
        </el-form-item>
        <el-form-item :label="$t('accountRedesign.label_description')" prop='description'>
          <el-input
            type='textarea'
            :autosize='{ minRows: 3, maxRows: 6 }'
            v-model.trim='companyApplyForm.description'
            :placeholder="$t('accountRedesign.placeholder_description')"
            maxlength='500'
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot='footer' class='apply-company-footer'>
        <el-button @click='applyDialogVisible = false'>{{ $t('accountRedesign.btn_cancel') }}</el-button>
        <el-button type='primary' :loading='applyLoading' @click='submitCompanyApply'>
          {{ $t('accountRedesign.btn_ok') }}
        </el-button>
      </div>
    </el-dialog>
  </modal>
</template>

<script>
import { mineApplyEnterprise } from '@/api/modules/material.js'
import { checkCompany, applyCompany } from '@/api/modules/account.js'

export default {
  props: ['isShowApply', 'title', 'currentEmail', 'currentMobile'],
  computed: {
    isShowDialog: {
      get () {
        return this.isShowApply
      },
      set (val) {
        this.$emit('update:isShowApply', val)
      }
    },
    applyRules () {
      return {
        companyCode: [
          { required: true, message: this.$t('accountRedesign.placeholder_company_id'), trigger: 'blur' },
          { pattern: /^[0-9a-zA-Z_]{1,30}$/, message: this.$t('accountRedesign.label_company_id'), trigger: 'blur' }
        ],
        email: [
          { required: true, message: this.$t('integration.email_address_cannot_be_empty'), trigger: 'blur' },
          { type: 'email', message: this.$t('integration.please_enter_the_correct_email_address'), trigger: ['blur', 'change'] }
        ],
        mobile: [
          { required: true, message: this.$t('integration.mobile_number_cannot_be_empty'), trigger: 'blur' },
          { max: 20, message: this.$t('integration.length_within_20'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('integration.please_input_a_password'), trigger: 'blur' }
        ]
      }
    },
    companyApplyRules () {
      return {
        companyName: [
          { required: true, message: this.$t('accountRedesign.placeholder_company_name'), trigger: 'blur' }
        ],
        contactName: [
          { required: true, message: this.$t('accountRedesign.placeholder_contact_name'), trigger: 'blur' }
        ],
        contactMobile: [
          { required: true, message: this.$t('accountRedesign.placeholder_contact_mobile'), trigger: 'blur' }
        ],
        contactEmail: [
          { required: true, message: this.$t('accountRedesign.placeholder_contact_email'), trigger: 'blur' },
          { type: 'email', message: this.$t('accountRedesign.placeholder_contact_email'), trigger: 'blur' }
        ]
      }
    }
  },
  data () {
    return {
      verifying: false,
      submitLoading: false,
      companyVerified: false,
      verifiedCompanyName: '',
      verifyErrorMsg: '',
      emailFromApply: false,
      mobileFromApply: false,
      applyForm: {
        companyCode: '',
        email: '',
        mobile: '',
        password: ''
      },
      // 申请 Company ID 弹框
      applyDialogVisible: false,
      applyLoading: false,
      companyApplyForm: {
        companyName: '',
        contactName: '',
        contactMobile: '',
        contactEmail: '',
        description: ''
      }
    }
  },
  watch: {
    isShowDialog (val) {
      if (val) {
        this.$nextTick(() => {
          this.resetSteps()
          if (this.$refs.applyForm) this.$refs.applyForm.resetFields()
        })
      }
    }
  },
  methods: {
    async verifyCompany () {
      try {
        await this.$refs.applyForm.validateField(['companyCode'])
      } catch (e) {
        return
      }
      if (!this.applyForm.companyCode) return
      this.verifying = true
      this.verifyErrorMsg = ''
      try {
        // checkCompany 成功 = Company ID 存在 = 验证通过
        const res = await checkCompany(this.applyForm.companyCode)
        if (res && res.state && res.state.code === 200) {
          this.companyVerified = true
          const result = res.result || {}
          this.verifiedCompanyName = result.companyName || ''
          // 从 Company ID 申请记录回显 email 和 mobile
          if (result.email) {
            this.applyForm.email = result.email
            this.emailFromApply = true
          }
          if (result.mobile) {
            this.applyForm.mobile = result.mobile
            this.mobileFromApply = true
          }
          this.$nextTick(() => {
            if (this.$refs.applyForm) this.$refs.applyForm.clearValidate(['companyCode'])
          })
        }
      } catch (e) {
        // checkCompany 失败 = Company ID 不存在
        this.verifyErrorMsg = this.$t('account_redesign.up_apply_company_not_exist')
      } finally {
        this.verifying = false
      }
    },
    resetVerify () {
      this.companyVerified = false
      this.verifiedCompanyName = ''
      this.verifyErrorMsg = ''
      this.emailFromApply = false
      this.mobileFromApply = false
      this.applyForm.email = ''
      this.applyForm.mobile = ''
    },
    resetSteps () {
      this.companyVerified = false
      this.verifiedCompanyName = ''
      this.verifyErrorMsg = ''
      this.emailFromApply = false
      this.mobileFromApply = false
      this.applyForm = {
        companyCode: '',
        email: '',
        mobile: '',
        password: ''
      }
    },
    // 打开申请 Company ID 弹框
    openApplyDialog () {
      this.companyApplyForm = {
        companyName: '',
        contactName: '',
        contactMobile: this.currentMobile || '',
        contactEmail: this.currentEmail || '',
        description: ''
      }
      this.applyDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.companyApplyForm) this.$refs.companyApplyForm.clearValidate()
      })
    },
    // 提交 Company ID 申请
    submitCompanyApply () {
      this.$refs.companyApplyForm.validate(valid => {
        if (!valid) return
        this.applyLoading = true
        const payload = {
          companyName: this.companyApplyForm.companyName,
          contactName: this.companyApplyForm.contactName,
          contactMobile: this.companyApplyForm.contactMobile,
          contactEmail: this.companyApplyForm.contactEmail,
          description: this.companyApplyForm.description
        }
        applyCompany(payload)
          .then(res => {
            if (res && res.state && res.state.code === 200) {
              this.$message.success(this.$t('accountRedesign.success_apply_submitted'))
              this.applyDialogVisible = false
            }
          })
          .catch(() => {})
          .finally(() => {
            this.applyLoading = false
          })
      })
    },
    async handleSubmit () {
      try {
        await this.$refs.applyForm.validate()
      } catch (e) {
        return
      }
      this.submitLoading = true
      try {
        const res = await mineApplyEnterprise({
          companyName: this.verifiedCompanyName,
          companyCode: this.applyForm.companyCode,
          email: this.applyForm.email,
          mobile: this.applyForm.mobile,
          password: this.applyForm.password
        })
        if (res && res.state && res.state.code === 200) {
          this.$message.success(this.$t('account_redesign.up_apply_success'))
          this.isShowDialog = false
          localStorage.removeItem('token')
          localStorage.removeItem('userId')
          localStorage.removeItem('customerId')
          this.$router.replace('/login')
        }
      } catch (e) {
        console.error('apply enterprise error:', e)
      } finally {
        this.submitLoading = false
      }
    }
  }
}
</script>

<style lang='scss' scoped>
.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;

  i {
    color: #e6a23c;
    font-size: 18px;
    margin-top: 2px;
  }

  span {
    color: #b88230;
    font-size: 13px;
    line-height: 1.5;
  }
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  .step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    .step-dot {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 2px solid #dcdfe6;
      background: #f5f7fa;
      color: #909399;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 600;
      transition: all 0.3s;

      &.active {
        border-color: var(--primary-color, #304e72);
        background: var(--primary-color, #304e72);
        color: #fff;
      }

      &.done {
        border-color: #67c23a;
        background: #67c23a;
        color: #fff;
      }
    }

    .step-label {
      font-size: 12px;
      color: #909399;
      padding: 3px 10px;
      border-radius: 12px;
      background: #f0f2f5;
      white-space: nowrap;
      transition: all 0.3s;

      &.active {
        color: #fff;
        background: var(--primary-color, #304e72);
        font-weight: 600;
      }

      &.done {
        color: #fff;
        background: #67c23a;
        font-weight: 600;
      }
    }
  }

  .step-line {
    width: 80px;
    height: 2px;
    background: #dcdfe6;
    margin: 0 12px;
    margin-bottom: 20px;
    transition: background 0.3s;

    &.done {
      background: #67c23a;
    }
  }
}

.company-verified-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 18px;

  i {
    color: #67c23a;
    font-size: 16px;
  }

  span {
    color: #529b2e;
    font-size: 13px;
  }
}

.verify-error-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 18px;
  margin-left: 140px;

  i {
    color: #f56c6c;
    font-size: 16px;
  }

  span {
    color: #a8161a;
    font-size: 13px;
  }
}

.apply-link-row {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  margin-left: 140px;

  .apply-link {
    flex: 1;
    text-align: right;
    font-size: 13px;
    color: var(--primary-color, #304e72);
    cursor: pointer;
    text-decoration: underline;
    line-height: 32px;

    &:hover {
      color: var(--primary-color-hover, #3d6190);
    }
  }

  .btn-spacer {
    visibility: hidden;
    pointer-events: none;
  }
}

.apply-form {
  .company-verify-row {
    display: flex;
    gap: 8px;

    .el-input {
      flex: 1;
    }
  }

  ::v-deep .el-form-item__label {
    white-space: nowrap;
  }
}

.apply-company-dialog {
  .apply-dialog-tip {
    font-size: 13px;
    color: #606266;
    margin: 0 0 16px 0;
    line-height: 1.5;
  }

  .apply-company-footer {
    display: flex;
    justify-content: flex-end;
    gap: 24px;
  }
}
</style>
